import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegion } from '@/contexts/RegionContext';
import { pricingTable, bundleSaving } from '@/lib/pricing';
import { Sparkles, Users, User, Award, Building2, ArrowRight } from 'lucide-react';

export const ThreeDoor = () => {
  const { t } = useTranslation();
  const { region, vatApplies } = useRegion();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="py-24 bg-ground border-b border-ground-deep">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-rule-3 mb-3 block">
            Custom Pathways
          </span>
          <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-4">
            How Would You Like to Partner With Us?
          </h2>
          <p className="text-ink/70 text-base font-light leading-relaxed">
            Whether you are an individual seeking executive debriefing, a coach expanding your practice, or an organization building team trust, explore tailored options below.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* 1st Window: For Individuals */}
          <div className="relative bg-ink text-white p-7 lg:p-6 xl:p-8 border-2 border-rule-1 border-t-4 border-t-rule-1 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all group flex flex-col h-full">
            <div className="flex items-center justify-between gap-2 mb-6">
              <span className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 bg-rule-1 text-white border border-white/20 flex items-center gap-1.5 rounded-xs">
                <User className="w-3 h-3 text-white" aria-hidden="true" />
                {t('threeDoor.myself.eyebrow', 'For Individuals')}
              </span>
              <span className="text-[10px] font-semibold text-white/70 uppercase tracking-wider flex items-center gap-1">
                <Users className="w-3 h-3 text-rule-1" aria-hidden="true" />
                1-on-1
              </span>
            </div>
            <h3 className="text-2xl font-serif text-white mb-4 group-hover:text-rule-1 transition-colors">
              {t('threeDoor.myself.title', 'Start your personal development journey')}
            </h3>
            <p className="text-white/80 mb-8 flex-grow leading-relaxed text-sm font-light">
              {t('threeDoor.myself.desc')}
            </p>
            <div className="mt-auto">
              <Link 
                to="/for-individuals#request-intro" 
                className="w-full py-3.5 px-4 bg-rule-1 hover:bg-white hover:text-ink text-white text-xs font-bold uppercase tracking-widest text-center flex items-center justify-center gap-2 transition-colors shadow-md group-hover:shadow-lg"
              >
                <span>{t('threeDoor.myself.link', 'Request a FIVE LENS Enneagram debrief')}</span>
                <ArrowRight className="w-3.5 h-3.5 text-white group-hover:text-ink group-hover:translate-x-1 transition-all" />
              </Link>
              <div className="pt-4 mt-5 border-t border-white/20 space-y-1.5">
                <div className="text-[11px] text-white/65 italic mb-1">
                  Delivered via accredited independent coaches
                </div>
                <Link 
                  to="/find-a-practitioner" 
                  className="text-xs font-medium text-white/85 hover:text-rule-1 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-1" aria-hidden="true" />
                  Find an Accredited Coach
                </Link>
              </div>
            </div>
          </div>

          {/* 2nd Window: For Coaches and Practitioners */}
          <div className="relative bg-white text-ink p-7 lg:p-6 xl:p-8 border border-rule-2/40 border-t-4 border-t-rule-2 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all group flex flex-col h-full">
            <div className="flex items-center justify-between gap-2 mb-6">
              <span className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 bg-rule-2/15 text-rule-2 border border-rule-2/30 flex items-center gap-1.5 rounded-xs">
                <Award className="w-3 h-3 text-rule-2" aria-hidden="true" />
                {t('threeDoor.practice.eyebrow', 'For Coaches and Practitioners')}
              </span>
            </div>
            <h3 className="text-2xl font-serif text-ink mb-4 group-hover:text-rule-2 transition-colors">
              {t('threeDoor.practice.title', 'Equip Your Coaching Toolkit')}
            </h3>
            <p className="text-ink/70 mb-8 flex-grow leading-relaxed text-sm font-light">
              {t('threeDoor.practice.desc')}
            </p>
            <div className="mt-auto">
              <Link 
                to="/practitioner-certification" 
                className="w-full py-3.5 px-4 bg-rule-1 hover:bg-ink text-white text-xs font-bold uppercase tracking-widest text-center flex items-center justify-center gap-2 transition-colors shadow-md group-hover:shadow-lg"
              >
                <span>{t('threeDoor.practice.link', 'View Certification Options')}</span>
                <ArrowRight className="w-3.5 h-3.5 text-white group-hover:translate-x-1 transition-all" />
              </Link>
              <div className="pt-4 mt-5 border-t border-ground-deep space-y-2">
                <Link 
                  to="/cohorts" 
                  className="text-xs font-medium text-ink/80 hover:text-rule-2 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-2" aria-hidden="true" />
                  Upcoming cohorts
                </Link>
                <Link 
                  to="/icf-cce-points" 
                  className="text-xs font-medium text-ink/80 hover:text-rule-2 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-2" aria-hidden="true" />
                  ICF CCE points
                </Link>
              </div>
            </div>
          </div>

          {/* 3rd Window: For Organisations & HR Teams */}
          <div className="relative bg-white text-ink p-7 lg:p-6 xl:p-8 border border-rule-5/40 border-t-4 border-t-rule-5 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all group flex flex-col h-full">
            <div className="flex items-center justify-between gap-2 mb-6">
              <span className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 bg-rule-5/15 text-rule-5 border border-rule-5/30 flex items-center gap-1.5 rounded-xs">
                <Building2 className="w-3 h-3 text-rule-5" aria-hidden="true" />
                {t('threeDoor.organisation.eyebrow', 'For Organisations & HR Teams')}
              </span>
            </div>
            <h3 className="text-2xl font-serif text-ink mb-4 group-hover:text-rule-5 transition-colors">
              {t('threeDoor.organisation.title', 'Resolve Systemic Team Friction')}
            </h3>
            <p className="text-ink/70 mb-8 flex-grow leading-relaxed text-sm font-light">
              {t('threeDoor.organisation.desc')}
            </p>
            <div className="mt-auto">
              <Link 
                to="/organizations" 
                className="w-full py-3.5 px-4 bg-rule-1 hover:bg-ink text-white text-xs font-bold uppercase tracking-widest text-center flex items-center justify-center gap-2 transition-colors shadow-md group-hover:shadow-lg"
              >
                <span>{t('threeDoor.organisation.link', 'Explore Enterprise Solutions')}</span>
                <ArrowRight className="w-3.5 h-3.5 text-white group-hover:translate-x-1 transition-all" />
              </Link>
              <div className="pt-4 mt-5 border-t border-ground-deep space-y-2">
                <Link 
                  to="/team-effectiveness" 
                  className="text-xs font-medium text-ink/80 hover:text-rule-5 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-5" aria-hidden="true" />
                  Team Alignment
                </Link>
                <Link 
                  to="/personal-mastery-and-resilience" 
                  className="text-xs font-medium text-ink/80 hover:text-rule-5 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-5" aria-hidden="true" />
                  Personal Mastery
                </Link>
              </div>
            </div>
          </div>

          {/* 4th Window: Practitioner + Team Facilitator Certification Bundle (Special Promo) */}
          <div className="relative bg-ink text-white p-7 lg:p-6 xl:p-8 border-2 border-rule-4 border-t-4 border-t-rule-4 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all group flex flex-col h-full">
            <div className="absolute -top-3.5 left-6 bg-rule-3 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-0.5 shadow-sm">
              Special Offer
            </div>
            <div className="flex items-center justify-between mb-4 mt-1">
              <span className="text-[11px] font-bold tracking-widest uppercase text-rule-4 flex items-center gap-1.5 min-h-[1.5em]">
                <Sparkles className="w-3.5 h-3.5 text-rule-4" aria-hidden="true" />
                {mounted ? (
                  region === 'za' ? `Bundle & Save ${bundleSaving.zar.formattedExclVat}` :
                  region === 'uk' ? `Bundle & Save ${bundleSaving.gbp.formattedAmount}` :
                  `Bundle & Save ${bundleSaving.usd.formattedAmount}`
                ) : 'Special Bundle Offer'}
              </span>
            </div>
            <h3 className="text-xl xl:text-2xl font-serif text-white mb-3 font-semibold leading-snug">
              Practitioner + Team Facilitator Certification Bundle
            </h3>
            <p className="text-xs text-white/80 mb-6 flex-grow leading-relaxed font-light">
              Take Level 1 and Level 2 together and save with our special combined tuition. Level 1 is completed first; Level 2 follows in the next Team Facilitator cohort.
            </p>
            <div className="mt-auto">
              {mounted ? (
                region === 'za' ? (
                  <div className="pt-4 border-t border-white/20 mb-5 space-y-1.5 text-xs">
                    <div className="flex items-baseline justify-between">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-serif font-bold text-white">{pricingTable['certification-bundle'].zar.formattedExclVat}</span>
                        <span className="text-[10px] text-white/70 uppercase">excl VAT</span>
                      </div>
                      <span className="text-xs text-white/70 line-through">R30 000</span>
                    </div>
                    {vatApplies && (
                      <div className="text-[11px] text-white/75">{pricingTable['certification-bundle'].zar.formattedInclVat} incl VAT (15%)</div>
                    )}
                  </div>
                ) : region === 'uk' ? (
                  <div className="pt-4 border-t border-white/20 mb-5 text-xs">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xl font-serif font-bold text-white">{pricingTable['certification-bundle'].gbp.formattedAmount}</span>
                      <span className="text-xs text-white/70 line-through">£2,250</span>
                    </div>
                    <div className="text-[11px] text-rule-4 font-semibold">Save {bundleSaving.gbp.formattedAmount} with bundle special</div>
                  </div>
                ) : (
                  <div className="pt-4 border-t border-white/20 mb-5 text-xs">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xl font-serif font-bold text-white">{pricingTable['certification-bundle'].usd.formattedAmount}</span>
                      <span className="text-xs text-white/70 line-through">$3,000</span>
                    </div>
                    <div className="text-[11px] text-rule-4 font-semibold">Save {bundleSaving.usd.formattedAmount} with bundle special</div>
                  </div>
                )
              ) : (
                <div className="pt-4 border-t border-white/20 mb-5 min-h-[3rem]" aria-hidden="true" />
              )}
              <Link 
                to="/register?product=certification-bundle" 
                className="text-xs font-bold tracking-widest uppercase text-center flex items-center justify-center gap-2 w-full py-3.5 px-4 bg-rule-3 hover:bg-rule-3/90 text-white transition-colors shadow-md"
              >
                <span>Enrol in Bundle</span>
                <ArrowRight className="w-3.5 h-3.5 text-white group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="pt-4 mt-5 border-t border-white/20 space-y-2">
                <Link 
                  to="/cohorts" 
                  className="text-xs font-medium text-white/80 hover:text-rule-4 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-4" aria-hidden="true" />
                  Upcoming cohorts
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
